/**
 * Ergebnisformat der Song-Analyse (docs/SONG-ANALYSE.md, Abschnitt 5) und
 * der Weg davon zum Taktraster-Text, den Lead Sheets schon versteht.
 *
 * Das Format enthaelt bewusst weder Audio noch Dateinamen – nur das, was
 * am Ende im Song stehen darf.
 */
import type { Bar, SectionKind } from './structure.ts';

export const ANALYSIS_SCHEMA = 'sr.leadsheet.analysis';
export const ANALYSIS_VERSION = 1;

export interface LeadSheetAnalysis {
  schema: typeof ANALYSIS_SCHEMA;
  version: typeof ANALYSIS_VERSION;
  createdAt: number;
  source: { kind: 'file'; durationSec: number };
  engine: { id: string; models: { name: string; version: string; license: string }[] };
  title: string;
  artist: string;
  tempo: { bpm: number; confidence: number };
  timeSignature: { beats: number; unit: number; confidence: number };
  key: { tonic: string; mode: 'major' | 'minor'; confidence: number };
  /** Beat-Zeitpunkte in Sekunden (fuer spaetere Synchronisation). */
  beats: number[];
  sections: {
    id: string;
    kind: SectionKind;
    label: string;
    startSec: number;
    endSec: number;
    repeat: number;
    confidence: number;
    bars: Pick<Bar, 'startSec' | 'chords'>[];
  }[];
  warnings: { code: string; message: string; sectionId?: string; barIndex?: number }[];
}

/** Grenzen der Anzeige (Abschnitt 5): ab 0,8 normal, darunter markiert. */
export const CONFIDENT = 0.8;
export const CHECK = 0.5;

/** Tonart fuer das Song-Feld: „Gm“, „Eb“. */
export function keyText(a: LeadSheetAnalysis): string {
  return `${a.key.tonic}${a.key.mode === 'minor' ? 'm' : ''}`;
}

/**
 * Eingaben aus dem Pruef-Schritt bereinigen: Ein Akkord darf das Raster
 * nicht aufbrechen (kein „|“, „,“, Zeilenumbruch), ein Name nicht zur
 * Taktzeile werden.
 */
export function cleanChord(text: string): string {
  return text.replace(/[^A-Za-z0-9#♯♭/+()°øΔ-]/g, '').slice(0, 16);
}
export function cleanLabel(text: string): string {
  return text.replace(/[\r\n:|,]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40) || 'Teil';
}

const cell = (bar: Pick<Bar, 'chords'>) => {
  const chords = bar.chords.filter((c) => c.symbol !== 'N').map((c) => cleanChord(c.symbol)).filter(Boolean);
  return chords.length ? chords.join(' ') : ' ';
};

/**
 * Taktraster-Text fuer `Song.body`:
 *
 *   Intro: | Fm | Fm | Gm | Gm |
 *   Verse 1: | Cm | Cm | Ab | Ab |  2x
 *
 * Laengere Abschnitte werden auf Zeilen zu je vier Takten verteilt; die
 * Wiederholung steht dann in einer eigenen Kommentarzeile, weil „2x“ im
 * Raster nur fuer die Zeile gilt, an der es steht.
 */
export function toSheetText(a: LeadSheetAnalysis): string {
  const out: string[] = [];
  for (const s of a.sections) {
    const label = cleanLabel(s.label);
    const rows: string[] = [];
    for (let i = 0; i < s.bars.length; i += 4) rows.push(`| ${s.bars.slice(i, i + 4).map(cell).join(' | ')} |`);
    if (rows.length === 0) continue;
    if (rows.length === 1) {
      out.push(`${label}: ${rows[0]}${s.repeat > 1 ? `  ${s.repeat}x` : ''}`);
    } else {
      out.push(`${label}: ${rows[0]}`, ...rows.slice(1));
      if (s.repeat > 1) out.push(`(${s.repeat}x)`);
    }
    out.push('');
  }
  return out.join('\n').trim() + '\n';
}

/**
 * Plausibilitaet pruefen, bevor das Ergebnis angezeigt wird. Liefert
 * Warnungen in einfachen Worten; nichts davon wirft.
 */
export function validate(a: LeadSheetAnalysis): LeadSheetAnalysis['warnings'] {
  const w: LeadSheetAnalysis['warnings'] = [];
  if (a.sections.length === 0) w.push({ code: 'no-music', message: 'Keine Akkorde gefunden – ist in der Datei Musik zu hören?' });
  if (a.tempo.confidence < CHECK) w.push({ code: 'tempo-unsure', message: 'Tempo unsicher – eventuell doppelt oder halb so schnell.' });
  if (a.key.confidence < CHECK) w.push({ code: 'key-unsure', message: 'Tonart unsicher – bitte prüfen.' });
  let all = 0;
  let unsure = 0;
  a.sections.forEach((s) =>
    s.bars.forEach((b, i) => {
      for (const c of b.chords) {
        if (c.symbol === 'N') continue;
        all++;
        if (c.confidence < CHECK) {
          unsure++;
          if (unsure <= 20) w.push({ code: 'chord-unsure', message: `${s.label}, Takt ${i + 1}: ${c.symbol} bitte prüfen.`, sectionId: s.id, barIndex: i });
        }
      }
    }),
  );
  if (all > 0 && unsure / all > 0.35) w.unshift({ code: 'chords-unsure', message: 'Unsichere Akkorderkennung – bitte überprüfen.' });
  return w;
}
